import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { CalendarOff, Clock, Layers, ListChecks, Network, Repeat, Settings2, Sparkles, UserPlus, Users, Wrench } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ShiftDefinitionsDialog } from '@/components/resource-center/ShiftDefinitionsDialog'
import { ShiftPatternsDialog } from '@/components/resource-center/ShiftPatternsDialog'
import { CenterListsDialog, type CenterListTab } from '@/components/resource-center/CenterListsDialog'
import { GroupsTab } from '@/components/resource-center/GroupsTab'
import { TeamsTab } from '@/components/resource-center/TeamsTab'
import { ResourcesList } from '@/components/resource-center/ResourcesList'
import { resourceCenterApi } from '@/api/resourceCenterApi'
import {
  useAssignmentGroups,
  useTeams,
  useInvalidateResourceCenter,
  useResources,
  useShiftDefinitions,
  useShiftPatterns,
  useSupportActivityTypes,
  useUnavailabilityTypes,
} from '@/hooks/useResourceCenter'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

type Editor = 'shifts' | 'patterns' | 'lists' | null

const TABS = ['people', 'teams', 'groups', 'setup'] as const
type PageTab = (typeof TABS)[number]

/**
 * Resource → User Center (epic z8nrz7dhzg): the Center Setup (z8nrz7f0vu) —
 * shifts, rotation patterns and SA / UA pick lists — then the groups and the
 * people list (z8nrz7f0vw). The calendar columns (R3b) build on the list.
 */
export function ResourcesPage() {
  const { canAny, can } = usePermission()
  const canRead = canAny([PERMISSIONS.resourceCenterRead, PERMISSIONS.resourceCapabilitiesRead])
  const canManage = can(PERMISSIONS.resourceCenterManage)
  // Groups and people are resource profiles, a different permission from the
  // Center Setup lists.
  const canManageProfiles = can(PERMISSIONS.resourceCapabilitiesManage)
  const [editor, setEditor] = useState<Editor>(null)
  const resources = useResources()
  const groups = useAssignmentGroups()
  const teams = useTeams()
  const [listTab, setListTab] = useState<CenterListTab>('support')
  // The tab lives in the URL (?tab=groups) so a link or a reload lands on it.
  const [params, setParams] = useSearchParams()
  const tab: PageTab = TABS.includes(params.get('tab') as PageTab) ? (params.get('tab') as PageTab) : 'people'
  const setTab = (t: string) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (t === 'people') next.delete('tab')
        else next.set('tab', t)
        return next
      },
      { replace: true },
    )

  const openLists = (tab: CenterListTab) => {
    setListTab(tab)
    setEditor('lists')
  }

  return (
    <div className='min-h-full w-full bg-muted/30 px-4 py-5 sm:px-6 lg:px-8'>
      <div className='w-full space-y-5'>
        <header className='flex flex-wrap items-center justify-between gap-4'>
          <div className='flex items-center gap-3'>
            <div className='flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-primary shadow-sm'>
              <UserPlus className='h-5 w-5 text-white' />
            </div>
            <div>
              <h1 className='text-2xl font-semibold tracking-tight text-foreground'>Resources</h1>
              <p className='text-sm text-muted-foreground'>
                Who works when, in which teams, and how much time is left for the work.
              </p>
            </div>
          </div>
          {canRead && (
            <div className='flex gap-2'>
              <Button size='sm' variant='outline' className='h-9 rounded-lg bg-background' onClick={() => setEditor('shifts')}>
                <Clock className='mr-1.5 h-4 w-4' /> Shift
              </Button>
              <Button size='sm' variant='outline' className='h-9 rounded-lg bg-background' onClick={() => setEditor('patterns')}>
                <Repeat className='mr-1.5 h-4 w-4' /> Patterns
              </Button>
              <Button size='sm' variant='outline' className='h-9 rounded-lg bg-background' onClick={() => openLists('support')}>
                <ListChecks className='mr-1.5 h-4 w-4' /> Center lists
              </Button>
            </div>
          )}
        </header>

        {canRead ? (
          <Tabs value={tab} onValueChange={setTab} className='space-y-4'>
            <TabsList className='h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent p-0'>
              {(
                [
                  ['people', 'People', Users, resources.data?.length],
                  ['teams', 'Teams', Network, teams.data?.length],
                  ['groups', 'Groups', Layers, groups.data?.length],
                  ['setup', 'Center setup', Settings2, undefined],
                ] as const
              ).map(([value, label, Icon, count]) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className='-mb-px gap-2 rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-sm text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
                >
                  <Icon className='h-4 w-4' /> {label}
                  {count !== undefined && (
                    <span className='rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>{count}</span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value='people' className='mt-0'>
              <ResourcesList />
            </TabsContent>
            <TabsContent value='teams' className='mt-0'>
              <TeamsTab canManage={canManageProfiles} />
            </TabsContent>
            <TabsContent value='groups' className='mt-0'>
              <GroupsTab canManage={canManageProfiles} />
            </TabsContent>
            <TabsContent value='setup' className='mt-0'>
              <CenterSetupSummary canManage={canManage} onOpen={setEditor} onOpenLists={openLists} />
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className='p-6 text-sm text-muted-foreground'>
              You don't have access to the center setup in this tenant.
            </CardContent>
          </Card>
        )}
      </div>

      {canRead && (
        <>
          <ShiftDefinitionsDialog
            open={editor === 'shifts'}
            onOpenChange={o => setEditor(o ? 'shifts' : null)}
            canManage={canManage}
          />
          <ShiftPatternsDialog
            open={editor === 'patterns'}
            onOpenChange={o => setEditor(o ? 'patterns' : null)}
            canManage={canManage}
          />
          <CenterListsDialog
            open={editor === 'lists'}
            onOpenChange={o => setEditor(o ? 'lists' : null)}
            canManage={canManage}
            tab={listTab}
            onTabChange={setListTab}
          />
        </>
      )}
    </div>
  )
}

function CenterSetupSummary({
  canManage,
  onOpen,
  onOpenLists,
}: {
  canManage: boolean
  onOpen: (editor: Editor) => void
  onOpenLists: (tab: CenterListTab) => void
}) {
  const shifts = useShiftDefinitions()
  const patterns = useShiftPatterns()
  const sa = useSupportActivityTypes()
  const ua = useUnavailabilityTypes()
  const invalidate = useInvalidateResourceCenter()
  const [loaded, setLoaded] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadDefaults = useMutation({
    mutationFn: resourceCenterApi.loadDefaults,
    onSuccess: r => {
      const added = r.shifts + r.patterns + r.supportActivityTypes + r.unavailabilityTypes
      setLoaded(added === 0 ? 'Nothing to add — the center already has every standard entry.' : `Added ${added} standard entries.`)
      setError(null)
      void invalidate()
    },
    onError: e => setError(e instanceof Error ? e.message : 'Something went wrong.'),
  })

  const lists = [shifts, patterns, sa, ua]
  const settled = lists.every(q => q.isSuccess)
  const allEmpty = settled && lists.every(q => q.data?.length === 0)
  const anyError = lists.find(q => q.error)?.error

  const activeCount = (rows?: { active: boolean }[]) => rows?.filter(r => r.active).length ?? 0

  return (
    <div className='space-y-4'>
      {anyError && <p className='text-sm text-destructive'>{anyError.message}</p>}
      {error && <p className='text-sm text-destructive'>{error}</p>}
      {loaded && <p className='text-sm text-emerald-700'>{loaded}</p>}

      {allEmpty && canManage && (
        <Card className='border-primary/30 bg-primary/[0.03]'>
          <CardContent className='flex flex-wrap items-center gap-4 p-5'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10'>
              <Sparkles className='h-5 w-5 text-primary' />
            </div>
            <div className='min-w-0 flex-1'>
              <p className='text-sm font-medium'>Start with the standard lists</p>
              <p className='text-sm text-muted-foreground'>
                Day, Evening, Night and 12-hour shifts, three common rotations, and the usual
                support and leave codes. You can edit or switch off any of them afterwards.
              </p>
            </div>
            <Button disabled={loadDefaults.isPending} onClick={() => loadDefaults.mutate()}>
              Load standard lists
            </Button>
          </CardContent>
        </Card>
      )}

      <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        <SummaryTile
          icon={Clock}
          title='Shifts'
          count={activeCount(shifts.data)}
          chips={shifts.data?.filter(s => s.active).map(s => s.code)}
          action='Edit shifts'
          onClick={() => onOpen('shifts')}
        />
        <SummaryTile
          icon={Repeat}
          title='Patterns'
          count={activeCount(patterns.data)}
          chips={patterns.data?.filter(p => p.active).map(p => p.name)}
          action='Edit patterns'
          onClick={() => onOpen('patterns')}
        />
        <SummaryTile
          icon={Wrench}
          title='Support activities'
          count={activeCount(sa.data)}
          chips={sa.data?.filter(s => s.active).map(s => s.code)}
          action='Edit list'
          onClick={() => onOpenLists('support')}
        />
        <SummaryTile
          icon={CalendarOff}
          title='Unavailability'
          count={activeCount(ua.data)}
          chips={ua.data?.filter(u => u.active).map(u => u.code)}
          action='Edit list'
          onClick={() => onOpenLists('unavailability')}
        />
      </div>

    </div>
  )
}

const MAX_CHIPS = 4

/**
 * One Center Setup list at a glance. Shift and SA/UA codes show as the same
 * chips the resource calendar will print in its cells, so the codes read the
 * same everywhere.
 */
function SummaryTile({
  icon: Icon,
  title,
  count,
  chips,
  action,
  onClick,
}: {
  icon: LucideIcon
  title: string
  count: number
  chips?: string[]
  action: string
  onClick: () => void
}) {
  const shown = chips?.slice(0, MAX_CHIPS) ?? []
  const more = (chips?.length ?? 0) - shown.length
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={`${action} (${count} active)`}
      title={action}
      className='flex flex-col rounded-xl border bg-background p-4 text-left transition-colors hover:border-primary/40 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
    >
      <span className='flex items-start justify-between gap-2'>
        <span className='flex items-center gap-2 text-sm font-medium'>
          <Icon className='h-4 w-4 text-muted-foreground' /> {title}
        </span>
        <span className='text-2xl font-semibold leading-none tabular-nums'>{count}</span>
      </span>
      <span className='mt-3 flex min-h-6 flex-wrap gap-1'>
        {shown.length === 0 && <span className='text-xs text-muted-foreground'>None yet</span>}
        {shown.map(c => (
          <span
            key={c}
            className='max-w-full truncate rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground/80'
          >
            {c}
          </span>
        ))}
        {more > 0 && <span className='px-1 py-0.5 text-xs text-muted-foreground'>+{more} more</span>}
      </span>
    </button>
  )
}
